import * as React from "react"
import { cn } from "@/lib/utils"

export interface OTPInputProps {
  /** Current value of the OTP */
  value: string
  /** Callback when value changes */
  onChange: (value: string) => void
  /** Number of digits (default: 6) */
  length?: number
  /** Whether the input is disabled */
  disabled?: boolean
  /** Error message to display */
  error?: string
  /** Fires when all digits have been entered */
  onComplete?: (value: string) => void
  /** Additional container className */
  containerClassName?: string
  /** Additional per-cell className */
  className?: string
}

const OTPInput = React.forwardRef<HTMLInputElement, OTPInputProps>(
  (
    {
      value,
      onChange,
      length = 6,
      disabled,
      error,
      onComplete,
      containerClassName,
      className,
    },
    ref
  ) => {
    const generatedId = React.useId()
    const errorId = error ? `${generatedId}-error` : undefined
    const inputsRef = React.useRef<Array<HTMLInputElement | null>>([])

    const digits = Array.from({ length }, (_, i) => value[i] ?? "")

    const emit = (next: string) => {
      const clean = next.replace(/\D/g, "").slice(0, length)
      onChange(clean)
      if (clean.length === length) onComplete?.(clean)
    }

    const handleChange = (index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value.replace(/\D/g, "")

      if (raw.length === 0) {
        emit(value.slice(0, index) + value.slice(index + 1))
        return
      }

      if (raw.length > 1) {
        emit(value.slice(0, index) + raw)
        const focusIdx = Math.min(index + raw.length, length - 1)
        inputsRef.current[focusIdx]?.focus()
        inputsRef.current[focusIdx]?.select()
        return
      }

      emit(value.slice(0, index) + raw + value.slice(index + 1))
      if (index < length - 1) {
        inputsRef.current[index + 1]?.focus()
        inputsRef.current[index + 1]?.select()
      }
    }

    const handleKeyDown = (index: number) => (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Backspace" && !value[index] && index > 0) {
        e.preventDefault()
        emit(value.slice(0, index - 1) + value.slice(index))
        inputsRef.current[index - 1]?.focus()
        inputsRef.current[index - 1]?.select()
      } else if (e.key === "ArrowLeft" && index > 0) {
        e.preventDefault()
        inputsRef.current[index - 1]?.focus()
        inputsRef.current[index - 1]?.select()
      } else if (e.key === "ArrowRight" && index < length - 1) {
        e.preventDefault()
        inputsRef.current[index + 1]?.focus()
        inputsRef.current[index + 1]?.select()
      }
    }

    const handlePaste = (index: number) => (e: React.ClipboardEvent<HTMLInputElement>) => {
      const pasted = e.clipboardData.getData("text").replace(/\D/g, "")
      if (!pasted) return
      e.preventDefault()
      emit(value.slice(0, index) + pasted)
      const focusIdx = Math.min(index + pasted.length, length - 1)
      inputsRef.current[focusIdx]?.focus()
      inputsRef.current[focusIdx]?.select()
    }

    return (
      <div className={cn("flex flex-col items-center", containerClassName)}>
        <div
          className="flex flex-wrap justify-center gap-1 pointer-coarse:gap-1.5"
          role="group"
          aria-label="Verification code"
        >
          {digits.map((digit, i) => (
            <input
              key={i}
              ref={(el) => {
                inputsRef.current[i] = el
                if (i === 0) {
                  if (typeof ref === "function") ref(el)
                  else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = el
                }
              }}
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={digit}
              onChange={handleChange(i)}
              onKeyDown={handleKeyDown(i)}
              onPaste={handlePaste(i)}
              onFocus={(e) => e.target.select()}
              // No maxLength: mobile keyboards (clipboard chips, OTP autofill) insert the
              // whole code as one text insertion with no paste event; maxLength would clip
              // it to one character before handleChange can distribute the digits.
              disabled={disabled}
              aria-label={`Digit ${i + 1}`}
              aria-invalid={error ? "true" : undefined}
              aria-describedby={errorId}
              className={cn(
                // Touch screens: 32×44 boxes (24×28 was too small to hit); six still fit at 320 px.
                "w-6 h-7 pointer-coarse:w-8 pointer-coarse:h-11 text-center text-sm pointer-coarse:text-base font-mono border rounded-md outline-none",
                "focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:border-transparent",
                "disabled:opacity-50 disabled:bg-gray-100 disabled:cursor-not-allowed",
                "transition-colors",
                error ? "border-red-500 bg-red-50" : "border-gray-300",
                className
              )}
            />
          ))}
        </div>
        {error && (
          <p id={errorId} className="mt-1 text-xs text-red-600" role="alert">
            {error}
          </p>
        )}
      </div>
    )
  }
)

OTPInput.displayName = "OTPInput"

export { OTPInput }
